import {
  AnchoredPanelType,
  CheckBox,
  FieldSet,
  FormValues,
  InfoBlockLayout,
  InfoBlockStatus,
  InputFormSidePanel,
  InputFormSidePanelProps,
  Select,
  SubmitButtonMode,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import * as Messages from "../../codegen/Messages";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import { SidePanelTestIds } from "../../constants/uiConstants";
import { DbBackupConfig } from "../../gen/clients/mchub-azure-api-client-exa";
import { backupRetentionPeriods, backupSlots, getBackUpScheduleTimeRange } from "../../helpers/resourceHelper";
import { utc } from "../../helpers/timeHelper";

export enum Fields {
  Backups = "backups",
  RetentionPeriod = "retention_period",
  Scheduling = "scheduling",
}

export enum FieldsTestIds {
  Backups = "backups-test",
  RetentionPeriod = "retention-period-test",
  Scheduling = "scheduling-test",
}

export enum InfoBlockTestIds {
  BackupInfo = "backup-info"
}

// eslint-disable-next-line max-len
export interface ExaDbCdbBackupPanelProps extends Pick <InputFormSidePanelProps, "onClose" | "onSubmit" | "componentRef"> {
  cdbBackup: DbBackupConfig|undefined;
}

export const ExaDbCdbBackupEditPanel = ({
  cdbBackup,
  onSubmit,
  onClose,
  componentRef,
}: ExaDbCdbBackupPanelProps): JSX.Element => {
  const { locale } = React.useContext<Settings>(SettingsContext);
  const [showBackupOptions, setShowBackupOptions] = React.useState<boolean>();
  const [defaultBackupSchedule, setDefaultBackupSchedule] = React.useState<string>();
  const [defaultRetentionPeriod, setDefaultRetentionPeriod] = React.useState<string>();
  const [key, setKey] = React.useState<string>();

  const [initialValues, setInitialValues] = React.useState<FormValues>();

  React.useEffect(() => {
    const initFieldValues: FormValues = {};

    if (cdbBackup) {
      const { autoBackupEnabled, autoBackupWindow, recoveryWindowInDays } = cdbBackup;

      initFieldValues[Fields.Backups] = autoBackupEnabled;
      setShowBackupOptions(autoBackupEnabled);

      if (autoBackupEnabled) {
        initFieldValues[Fields.Scheduling] = [autoBackupWindow];
        initFieldValues[Fields.RetentionPeriod] = [recoveryWindowInDays?.toString()];

        setDefaultBackupSchedule(autoBackupWindow);
        setDefaultRetentionPeriod(recoveryWindowInDays?.toString());
      }
    } else {
      initFieldValues[Fields.Backups] = false;
      setShowBackupOptions(false);
    }

    setInitialValues(initFieldValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cdbBackup]);

  const info = {
    messageType: InfoBlockStatus.INFO,
    message: Messages.createExaDb.managementTab.info.backupInfo(),
    infoLayout: InfoBlockLayout.Compact,
    testId: InfoBlockTestIds.BackupInfo,
  };

  const onChangeShowBackUpOptions = (value: boolean): void => {
    setShowBackupOptions(value);
    if (value) {
      setKey(uniqueGUID);
    }
  };

  const retentionOptions = Object.values(backupRetentionPeriods).map(period => ({
    id: `${period}`,
    text: `${period} ${Messages.common.days()}`,
  }));

  const scheduleOptions = backupSlots.map(schedule => ({
    id: schedule.id,
    text: getBackUpScheduleTimeRange(schedule.id, locale),
  }));

  return (
    <InputFormSidePanel
      type={AnchoredPanelType.MEDIUM}
      title={Messages.createNewPanels.editBackup.title()}
      submitButtonMode={SubmitButtonMode.DISABLE_TILL_VALID}
      onSubmit={onSubmit}
      onClose={onClose}
      componentRef={componentRef}
      testId={SidePanelTestIds.ExaDbCdbBackupEditPanel}
      initialValues={initialValues}
    >
      <FieldSet
        header={Messages.labels.backup()}
      >
        <CheckBox
          testId={FieldsTestIds.Backups}
          defaultValue={showBackupOptions}
          statusInfo={info}
          fieldName={Fields.Backups}
          label={Messages.labels.enableAutomaticBackups()}
          onChange={value => {
            onChangeShowBackUpOptions(value);
          }}
        />
        {showBackupOptions && (
          <>
            <Select
              testId={FieldsTestIds.RetentionPeriod}
              key={`${key}-1`}
              defaultValue={defaultRetentionPeriod ? [defaultRetentionPeriod] : undefined}
              required
              fieldName={Fields.RetentionPeriod}
              label={Messages.labels.backupRetentionPeriod()}
              options={retentionOptions}
              onChange={(id: string) => setDefaultRetentionPeriod(id)}
              hideSearchBox
            />
            <Select
              testId={FieldsTestIds.Scheduling}
              key={`${key}-2`}
              defaultValue={defaultBackupSchedule ? [defaultBackupSchedule] : undefined}
              required
              fieldName={Fields.Scheduling}
              label={`${Messages.labels.backupScheduling()} (${utc})`}
              options={scheduleOptions}
              onChange={(id: string) => setDefaultBackupSchedule(id)}
              hideSearchBox
            />
          </>
        )}
      </FieldSet>
    </InputFormSidePanel>
  );
};
